import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ArrowLeftIcon } from '../components/Icons';
import { strategyService, Strategy } from '../services/strategyService';
import { api } from '../services/api';
import OnboardingModule from '../components/modules/OnboardingModule';
import Module1Problem from '../components/modules/Module1Problem';
import Module2Insight from '../components/modules/Module2Insight';
import Module3Direction from '../components/modules/Module3Direction';
import Module4Truth from '../components/modules/Module4Truth';
import Module5Integration from '../components/modules/Module5Integration';
import Module6Actionables from '../components/modules/Module6Actionables';
import FinalSummary from '../components/modules/FinalSummary';
import ModuleXDecision from '../components/modules/ModuleXDecision';
import ModuleYUpload from '../components/modules/ModuleYUpload';
import ModuleZDiagnostic from '../components/modules/ModuleZDiagnostic';
import Module6BActionables from '../components/modules/Module6BActionables';


const StrategyWorkflow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [strategy, setStrategy] = useState<Strategy | null>(null);
  const [loading, setLoading] = useState(id !== 'new');
  const [error, setError] = useState('');

  // Determinar si se activó la ruta B
  let isRutaB = false;
  if (strategy?.module_x_decision) {
    try {
      const dec = typeof strategy.module_x_decision === 'string' 
        ? JSON.parse(strategy.module_x_decision) 
        : strategy.module_x_decision;
      isRutaB = dec.activar_diagnostico_inversion === 'Sí';
    } catch (e) {
      console.error('Error parsing module_x_decision:', e);
    }
  }

  // Definir los pasos del workflow de forma dinámica
  const baseSteps = [
    'Onboarding',
    'El Problema',
    'El Insight',
    'Dirección Creativa',
    'Verdad Activadora',
    'Integración'
  ];

  let steps = [...baseSteps];
  if (strategy && strategy.current_module >= 5) {
    steps.push('Bifurcación');
    if (isRutaB) {
      steps.push('Subida Datos', 'Diagnóstico Inversión', 'Accionables Integrados');
    } else {
      steps.push('Accionables Tradicionales');
    }
    steps.push('Resumen Final');
  } else {
    steps.push('Accionables', 'Resumen Final');
  }

  const mapModuleToStep = (moduleNum: number, isB: boolean): number => {
    if (moduleNum <= 5) return moduleNum;
    if (moduleNum === 10) return 6; // Bifurcación
    if (isB) {
      if (moduleNum === 11) return 7; // Subida Datos
      if (moduleNum === 12) return 8; // Diagnóstico
      if (moduleNum === 13) return 9; // Accionables Integrados
      if (moduleNum >= 7) return 10;   // Resumen Final
    } else {
      if (moduleNum === 6) return 7;   // Accionables Tradicionales
      if (moduleNum >= 7) return 8;    // Resumen Final
    }
    return moduleNum;
  };

  useEffect(() => {
    if (id === 'new') {
      setCurrentStep(0);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const data = await strategyService.getById(id!);
        setStrategy(data);
        
        let isB = false;
        if (data.module_x_decision) {
          try {
            const dec = typeof data.module_x_decision === 'string' 
              ? JSON.parse(data.module_x_decision) 
              : data.module_x_decision;
            isB = dec.activar_diagnostico_inversion === 'Sí';
          } catch(e){}
        }

        const step = mapModuleToStep(data.current_module, isB);
        setCurrentStep(step);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al cargar la estrategia');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleNext = async (newStrategyId?: string) => {
    if (typeof newStrategyId === 'string' && newStrategyId) {
      navigate(`/strategies/${newStrategyId}/workflow`, { replace: true });
      return;
    }
    if (id) {
      try {
        const data = await strategyService.getById(id);
        setStrategy(data);
        
        let isB = false;
        if (data.module_x_decision) {
          try {
            const dec = typeof data.module_x_decision === 'string' 
              ? JSON.parse(data.module_x_decision) 
              : data.module_x_decision;
            isB = dec.activar_diagnostico_inversion === 'Sí';
          } catch(e){}
        }

        const step = mapModuleToStep(data.current_module, isB);
        setCurrentStep(step);
      } catch (e) {
        console.error('No se pudo recargar la estrategia después de completar el módulo:', e);
        alert('El módulo se procesó, pero no se pudo recargar el estado. Revisa la conexión y vuelve a intentarlo.');
      }
    } else {
      if (currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/strategies/${id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Estrategia_JJAgency.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando PDF:', err);
      alert('Error al exportar el PDF. Asegúrate de estar autenticado.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Cargando estrategia...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Integrated Strategic Workflow</h1>
        </div>

        {/* Wizard Progress Bar */}
        <div style={styles.wizardContainer}>
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <div key={step} style={styles.stepWrapper}>
                <div style={{
                  ...styles.stepCircle,
                  background: isCompleted ? 'var(--success)' : isActive ? 'var(--text-primary)' : 'var(--bg-hover)',
                  color: isActive || isCompleted ? 'var(--bg-card)' : 'var(--text-muted)',
                  border: isCompleted || isActive ? 'none' : '2px solid var(--border)'
                }}>
                  {index === 0 ? '✓' : index}
                </div>
                <div style={{
                  ...styles.stepLabel,
                  color: isCompleted ? 'var(--success)' : isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontWeight: isActive ? 600 : 400
                }}>
                  {step}
                </div>
                {index < steps.length - 1 && (
                  <div style={{
                    ...styles.stepLine,
                    background: isCompleted ? 'var(--success)' : 'var(--border)'
                  }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Modular Content */}
        <div style={styles.moduleContainer}>
          {currentStep === 0 && <OnboardingModule onComplete={handleNext} strategyId={id} />}
          {currentStep === 1 && <Module1Problem onComplete={handleNext} strategyId={id} />}
          {currentStep === 2 && <Module2Insight onComplete={handleNext} strategyId={id} />}
          {currentStep === 3 && <Module3Direction onComplete={handleNext} strategyId={id} />}
          {currentStep === 4 && <Module4Truth onComplete={handleNext} strategyId={id} />}
          {currentStep === 5 && <Module5Integration onComplete={handleNext} strategyId={id} />}
          
          {/* bifurcated navigation */}
          {currentStep === 6 && <ModuleXDecision onComplete={handleNext} strategyId={id} />}
          
          {/* Ruta B */}
          {isRutaB ? (
            <>
              {currentStep === 7 && <ModuleYUpload onComplete={handleNext} strategyId={id} />}
              {currentStep === 8 && <ModuleZDiagnostic onComplete={handleNext} strategyId={id} />}
              {currentStep === 9 && <Module6BActionables onComplete={handleNext} strategyId={id} />}
              {currentStep === 10 && <FinalSummary strategyId={id} />}
            </>
          ) : (
            <>
              {currentStep === 7 && <Module6Actionables onComplete={handleNext} strategyId={id} />}
              {currentStep === 8 && <FinalSummary strategyId={id} />}
            </>
          )}
        </div>

        <div style={{ marginTop: '32px', borderTop: '1px solid var(--border)', paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => {
            if (currentStep > 0) {
              setCurrentStep(prev => prev - 1);
            } else {
              navigate(-1);
            }
          }} style={styles.backBtn}>
            <ArrowLeftIcon size={16} color="var(--text-secondary)" />
            <span>Volver</span>
          </button>

          {currentStep > 0 && (
            <button onClick={handleExport} style={{ ...styles.backBtn, background: 'var(--bg-hover)', color: 'var(--accent)', border: '1px solid var(--accent)' }}>
              Exportar PDF
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1280px',
    width: '100%',
    margin: '0 auto',
    padding: '24px',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
    overflowX: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '32px',
    boxSizing: 'border-box'
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'transparent',
    border: '1px solid var(--border)',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    color: 'var(--text-primary)',
    boxSizing: 'border-box'
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
    boxSizing: 'border-box'
  },
  wizardContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '48px',
    background: 'var(--bg-card)',
    padding: '24px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    boxShadow: 'var(--shadow)',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  stepWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    flex: '1 0 120px',
    minWidth: '120px',
    boxSizing: 'border-box'
  },
  stepCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '8px',
    zIndex: 2,
    boxSizing: 'border-box'
  },
  stepLabel: {
    fontSize: '12px',
    textAlign: 'center',
    position: 'absolute',
    top: '40px',
    width: '120px',
    boxSizing: 'border-box',
    whiteSpace: 'normal'
  },
  stepLine: {
    position: 'absolute',
    height: '2px',
    top: '16px',
    left: 'calc(50% + 16px)',
    width: 'calc(100% - 32px)',
    zIndex: 1,
    boxSizing: 'border-box'
  },
  moduleContainer: {
    background: 'var(--bg-card)',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    padding: '24px',
    boxShadow: 'var(--shadow)',
    minHeight: '400px',
    boxSizing: 'border-box',
    maxWidth: '100%',
    overflowX: 'hidden'
  }
};

export default StrategyWorkflow;
